import React, { Suspense, useContext, useEffect } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { UserContext } from "./Context/UserContext";
import { getToken } from "./utils/authToken";
import { useTranslation } from "react-i18next";
import ProtectedRoute from "./Components/ProtectedRoute/ProtectedRoute";
import PermissionProtectedRoute from "./Components/ProtectedRoute/PermissionProtectedRoute";
import PageLoading from "./Components/Loading/PageLoading";
import { PERMISSIONS } from "./utils/permissions";

// Context Providers (keep synchronous as they're needed immediately)
import AnimalContextProvider from "./Context/AnimalContext";
import MatingContextProvider from "./Context/MatingContext";
import WeightContextProvider from "./Context/WeightContext.js";
import BreedingcontextProvider from "./Context/BreedingContext.jsx";
import GetAnimalContextProvider from "./Context/GetAnimalContext.js";
import VaccineanimalContextProvider from "./Context/VaccineanimalContext.js";
import ExcludedContextProvider from "./Context/ExcludedContext.js";
import TreatmentContextProvider from "./Context/TreatmentContext.js";
import FeedContextProvider from "./Context/FeedContext.js";
import FeedbyLocationContextProvider from "./Context/FeedbylocationContext.jsx";
import DashboardContextProvider from "./Context/DashboardContext.js";
import LocationContextProvider from "./Context/Locationshedcontext.js";
import LocationContextshedProvider from "./Context/LocationContext.js";
import BreedContextProvider from "./Context/BreedContext.js";
import VaccinetableentriescontextProvider from "./Context/Vaccinetableentriescontext.jsx";
import NewvaccineContextProvider from "./Context/NewvaccineContext.jsx";
import SupplierContextProvider from "./Context/SupplierContext.js";
import { SidebarProvider } from './Context/SidebarContext.jsx';
import AdminContextProvider from "./Context/AdminContext.jsx";
import VaccineTypeContextProvider from "./Context/VaccineTypeContext.js";
import PlanContextProvider from "./Context/PlanContext.js";
import DiscountCodeContextProvider from "./Context/DiscountCodeContext.js";
import { SubscriptionProvider } from './Context/SubscriptionContext';
import { CourseAuthProvider } from './features/courses/context/CourseAuthContext';

// Layout (keep synchronous as it's the root)
import Layout from "./Components/Layout/Layout";

// Alias `lazy` to the retry-safe helper so existing lazy imports get protection.
const lazy = lazyWithRetry;

// Lazy load all page components for code splitting
const Home = lazyWithRetry(() => import("./Components/Home/Home"));
const Login = lazyWithRetry(() => import("./Components/Login/Login"));
const Register = lazyWithRetry(() => import("./Components/Register/Register"));
const Animals = lazyWithRetry(() => import("./Components/Animals/Animals"));
const AnimalsDetails = lazyWithRetry(() => import("./Components/Animals/AnimalsDetails"));
const Mating = lazyWithRetry(() => import("./Components/Mating/Mating"));
const Weight = lazyWithRetry(() => import("./Components/Weight/Weight"));
const EditAnimal = lazyWithRetry(() => import("./Components/Animals/EditAnimal"));
const MatingTable = lazyWithRetry(() => import("./Components/Mating/MatingTable.jsx"));
const EditMating = lazyWithRetry(() => import("./Components/Mating/EditMating.jsx"));
const Resetpassword = lazyWithRetry(() => import("./Components/Login/Resetpassword.jsx"));
const Forgetpassword = lazyWithRetry(() => import("./Components/Login/Forgetpassword.jsx"));
const Verifyotp = lazyWithRetry(() => import("./Components/Login/Verifyotp.jsx"));
const Breeding = lazy(() => import("./Components/Breeding/Breeding.jsx"));
const WeightTable = lazy(() => import("./Components/Weight/WeightTable.jsx"));
const EditWeight = lazy(() => import("./Components/Weight/EditWeight.jsx"));
const BreadingTable = lazy(() => import("./Components/Breeding/BreadingTable.jsx"));
const EditBreeding = lazy(() => import("./Components/Breeding/EditBreeding.jsx"));
const AddBreeding = lazy(() => import("./Components/Breeding/AddBreeding.jsx"));
const Report = lazy(() => import("./Components/Report/Report.jsx"));
const ReportDaliy = lazy(() => import("./Components/Report/ReportDaliy.jsx"));
const Vaccinebyanimal = lazy(() => import("./Components/Vaccine/Vaccinebyanimal.jsx"));
const Vaccinebyanimalsstable = lazy(() => import("./Components/Vaccine/Vaccinebyanimalstable.js"));
const Vaccinebylocationshed = lazy(() => import("./Components/Vaccine/Vaccinebylocationshed.jsx"));
const VaccineTable = lazy(() => import("./Components/Vaccine/VaccineTable.jsx"));
const EditVaccine = lazy(() => import("./Components/Vaccine/EditVaccine.jsx"));
const HomeServices = lazy(() => import("./Components/Home/HomeServices.jsx"));
const Excluded = lazy(() => import("./Components/Excluded/Excluded.jsx"));
const Excludedtable = lazy(() => import("./Components/Excluded/Excludedtable.jsx"));
const EditExcluded = lazy(() => import("./Components/Excluded/EditExcluded.jsx"));
const Treatment = lazy(() => import("./Components/Treatment/Treatment.jsx"));
const TreatmentTable = lazy(() => import("./Components/Treatment/TreatmentTable.jsx"));
const EditTreatment = lazy(() => import("./Components/Treatment/EditTreatment.jsx"));
const TreatmentLocation = lazy(() => import("./Components/Treatment/TreatmentLocation.jsx"));
const TreatmentAnimal = lazy(() => import("./Components/Treatment/TreatmentAnimal.jsx"));
const AnimalCost = lazy(() => import("./Components/Animals/AnimalCost.jsx"));
const TreatAnimalTable = lazy(() => import("./Components/Treatment/TreatAnimalTable.jsx"));
const EditTreatAnimal = lazy(() => import("./Components/Treatment/EditTreatAnimal.jsx"));
const Editfeed = lazy(() => import("./Components/Feeding/Editfeed.jsx"));
const EditFeedbyLocation = lazy(() => import("./Components/Feeding/Editfeedbylocation.jsx"));
const Feedbylocation = lazy(() => import("./Components/Feeding/Feedbylocation.jsx"));
const FeedingTable = lazy(() => import("./Components/Feeding/FeedingTable.jsx"));
const Feedlocationtable = lazy(() => import("./Components/Feeding/Feedlocationtable.jsx"));
const Feed = lazy(() => import("./Components/Feeding/Feed.jsx"));
const Fodder = lazy(() => import("./Components/Feeding/Fodder.jsx"));
const FodderTable = lazy(() => import("./Components/Feeding/FodderTable.jsx"));
const ViewDetailsofAnimal = lazy(() => import("./Components/Animals/ViewDetailsofAnimal.jsx"));
const ScanAnimal = lazy(() => import("./Components/Animals/ScanAnimal.jsx"));
const EditFodder = lazy(() => import("./Components/Feeding/EditFodder.jsx"));
const Dashboard = lazy(() => import("./Components/Dashboard/Dashboard.jsx"));
const NotAuthorized = lazy(() => import("./Components/Dashboard/NotAuthorized.jsx"));
const EditVaccineanimals = lazy(() => import("./Components/Vaccine/EditVaccineanimals.jsx"));
const Vaccinebytagid = lazy(() => import("./Components/Vaccine/Vaccinebytagid.jsx"));
const AnimalServices = lazy(() => import("./Components/Services/AnimalServices.jsx"));
const MatingServices = lazy(() => import("./Components/Services/MatingServices.jsx"));
const WeightServices = lazy(() => import("./Components/Services/WeightServices.jsx"));
const BreedingServices = lazy(() => import("./Components/Services/BreedingServices.jsx"));
const VaccineServices = lazy(() => import("./Components/Services/VaccineServices.jsx"));
const ExcludedServices = lazy(() => import("./Components/Services/ExcludedServices.jsx"));
const TreetmentServices = lazy(() => import("./Components/Services/TreetmentSerivces.jsx"));
const FeedingServices = lazy(() => import("./Components/Services/FeedingServices.jsx"));
const FodderServices = lazy(() => import("./Components/Services/FodderServices.jsx"));
const MatingLocation = lazy(() => import("./Components/Mating/MatingLocation.jsx"));
const LocationPost = lazy(() => import("./Components/LocationShed/LocationPost.jsx"));
const LocationTable = lazy(() => import("./Components/LocationShed/LocationTable.jsx"));
const EditLocation = lazy(() => import("./Components/LocationShed/EditLocation.jsx"));
const BreedPost = lazy(() => import("./Components/Breed/BreedPost.jsx"));
const BreedTable = lazy(() => import("./Components/Breed/BreedTable.jsx"));
const EditBreed = lazy(() => import("./Components/Breed/EditBreed.jsx"));
const LocationServices = lazy(() => import("./Components/Services/LocationServices.jsx"));
const BreedServices = lazy(() => import("./Components/Services/BreedServices.jsx"));
const Use = lazy(() => import("./Components/Home/Usefullto.jsx"));
const Features = lazy(() => import("./Components/LastSection/LastSection.jsx"));
const Manageemployee = lazy(() => import("./Components/Section/Section.jsx"));
const ReportServices = lazy(() => import("./Components/Services/ReportServices.jsx"));
const NotificationPage = lazy(() => import("./Components/Notification/NotificationPage.jsx"));
const Support = lazy(() => import("./Components/Support/Support.jsx"));
const Pharmacy = lazy(() => import("./Components/Services/Pharmacy.jsx"));
const WithGrowthData = lazy(() => import("./Components/Weight/WithGrowthData.jsx"));
const Supplier = lazy(() => import("./Components/Suppliers/Supplier.jsx"));
const SupplierTable = lazy(() => import("./Components/Suppliers/SupplierTable.jsx"));
const EditSupplier = lazy(() => import("./Components/Suppliers/EditSupplier.jsx"));
const SupplierServices = lazy(() => import("./Components/Services/SupplierServices.jsx"));
const DetailsLocation = lazy(() => import("./Components/LocationShed/DetailsLocation.jsx"));
const AdminDashboard = lazy(() => import("./Components/AdminDashboard/AdminDashboard.jsx"));
const AddVaccineType = lazy(() => import("./Components/VaccineType/AddVaccineType.jsx"));
const VaccineTypetable = lazy(() => import("./Components/VaccineType/VaccineTypetable.jsx"));
const UserDashboard = lazy(() => import("./Components/Dashboard/UserDashboard.jsx"));
const WeeklyNotifications = lazy(() => import("./Components/WeeklyNotifications/WeeklyNotifications.jsx"));
const PlansPage = lazy(() => import("./pages/PlansPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const PaymentFailedPage = lazy(() => import("./pages/PaymentFailedPage"));
const EditVaccineType = lazy(() => import("./Components/VaccineType/EditVaccineType.jsx"));
const AddPlan = lazy(() => import("./Components/Plan/AddPlan.jsx"));
const PlanTable = lazy(() => import("./Components/Plan/PlanTable.jsx"));
const EditPlan = lazy(() => import("./Components/Plan/EditPlan.jsx"));
const ListPlan = lazy(() => import("./Components/Plan/ListPlan.jsx"));
const AddDiscountCode = lazy(() => import("./Components/DiscountCode/AddDiscountCode.jsx"));
const DiscountCodeTable = lazy(() => import("./Components/DiscountCode/DiscountCodeTable.jsx"));
const EditDiscountCode = lazy(() => import("./Components/DiscountCode/EditDiscountCode.jsx"));
const RolesList = lazy(() => import("./Components/Roles/RolesList.jsx"));
const RoleForm = lazy(() => import("./Components/Roles/RoleForm.jsx"));
const EmployeesList = lazy(() => import("./Components/Employees/EmployeesList.jsx"));
const EmployeeForm = lazy(() => import("./Components/Employees/EmployeeForm.jsx"));
const EmployeeRoles = lazy(() => import("./Components/Employees/EmployeeRoles.jsx"));
const Profile = lazy(() => import("./Components/Profile/Profile.jsx"));

// Support Tickets Pages
const PublicSupportForm = lazy(() => import("./features/support/pages/PublicSupportForm.jsx"));
const UserTicketsList = lazy(() => import("./features/support/pages/UserTicketsList.jsx"));
const UserTicketDetails = lazy(() => import("./features/support/pages/UserTicketDetails.jsx"));
const AdminTicketsList = lazy(() => import("./features/support/pages/AdminTicketsList.jsx"));
const AdminTicketDetails = lazy(() => import("./features/support/pages/AdminTicketDetails.jsx"));
const AdminSupportStatistics = lazy(() => import("./features/support/pages/AdminSupportStatistics.jsx"));

// Course Registration Module
const CourseLanding = lazy(() => import("./features/courses/pages/public/CourseLanding.jsx"));
const CourseRegister = lazy(() => import("./features/courses/pages/public/CourseRegister.jsx"));
const CourseLogin = lazy(() => import("./features/courses/pages/public/CourseLogin.jsx"));
const GroupRegistrationSuccess = lazy(() => import("./features/courses/pages/public/GroupRegistrationSuccess.jsx"));
const CourseLayout = lazy(() => import("./features/courses/components/CourseLayout.jsx"));
const CourseProtectedRoute = lazy(() => import("./features/courses/components/CourseProtectedRoute.jsx"));
const CourseDashboard = lazy(() => import("./features/courses/pages/student/CourseDashboard.jsx"));
const CoursePayment = lazy(() => import("./features/courses/pages/student/CoursePayment.jsx"));
const CourseLectures = lazy(() => import("./features/courses/pages/student/CourseLectures.jsx"));
const CourseProfile = lazy(() => import("./features/courses/pages/student/CourseProfile.jsx"));
const AdminCourseDashboard = lazy(() => import("./features/courses/pages/admin/AdminCourseDashboard.jsx"));
const AdminCourseStudents = lazy(() => import("./features/courses/pages/admin/AdminCourseStudents.jsx"));
const AdminCourseRegistrations = lazy(() => import("./features/courses/pages/admin/AdminCourseRegistrations.jsx"));
const AdminCourseRegistrationDetails = lazy(() => import("./features/courses/pages/admin/AdminCourseRegistrationDetails.jsx"));
const AdminCoursePayments = lazy(() => import("./features/courses/pages/admin/AdminCoursePayments.jsx"));
const AdminCoursePaymentDetails = lazy(() => import("./features/courses/pages/admin/AdminCoursePaymentDetails.jsx"));
const AdminCourseLectures = lazy(() => import("./features/courses/pages/admin/AdminCourseLectures.jsx"));
const AdminCoursePaymentSettings = lazy(() => import("./features/courses/pages/admin/AdminCoursePaymentSettings.jsx"));

// Wrapper component for Suspense
const LazyWrapper = ({ children }) => (
  <Suspense fallback={<PageLoading variant="fullscreen" />}>{children}</Suspense>
);

let routers = createBrowserRouter([
  {
    path: "", 
    element: <Layout />, 
    children: [
      { index: true, element: <Home/> },
      { path: "homeServices", element: <LazyWrapper><ProtectedRoute><HomeServices/></ProtectedRoute></LazyWrapper> },
      { path: "report", element: <LazyWrapper><ProtectedRoute><Report/></ProtectedRoute></LazyWrapper> },
      { path: "reportDaliy", element: <LazyWrapper><ProtectedRoute><ReportDaliy/></ProtectedRoute></LazyWrapper> },
      { path: "excluded", element: <LazyWrapper><ProtectedRoute><Excluded/></ProtectedRoute></LazyWrapper> },
      { path: "excludedtable", element: <LazyWrapper><ProtectedRoute><Excludedtable/></ProtectedRoute></LazyWrapper> },
      { path: "editExcluded/:id", element: <LazyWrapper><ProtectedRoute><EditExcluded/></ProtectedRoute></LazyWrapper> },
      { path: "notAuthorized", element: <LazyWrapper><NotAuthorized /></LazyWrapper> },

      { path: "login", element: <LazyWrapper><Login /></LazyWrapper> },
      { path: "verifyotp", element: <LazyWrapper><Verifyotp /></LazyWrapper> },
      { path: "forgetpassword", element: <LazyWrapper><Forgetpassword /></LazyWrapper> },
      { path: "resetpassword", element: <LazyWrapper><Resetpassword /></LazyWrapper> },
      { path: "scan/:token", element: <LazyWrapper><ScanAnimal /></LazyWrapper> },
      { path: "/Use", element: <LazyWrapper><Use /></LazyWrapper> },
      { path: "/Manageemployee", element: <LazyWrapper><Manageemployee /></LazyWrapper> },
      { path: "features", element: <LazyWrapper><Features /></LazyWrapper> },
      { path: "register", element: <LazyWrapper><Register /></LazyWrapper> },
      { path: "plans", element: <LazyWrapper><PlansPage /></LazyWrapper> },
      { path: "listPlan", element: <LazyWrapper><ListPlan /></LazyWrapper> },
      { path: "subscription/plans", element: <LazyWrapper><PlansPage /></LazyWrapper> },
      { path: "subscription/checkout", element: <LazyWrapper><ProtectedRoute><CheckoutPage /></ProtectedRoute></LazyWrapper> },
      { path: "subscription/success", element: <LazyWrapper><ProtectedRoute><PaymentSuccessPage /></ProtectedRoute></LazyWrapper> },
      { path: "subscription/failed", element: <LazyWrapper><ProtectedRoute><PaymentFailedPage /></ProtectedRoute></LazyWrapper> },
      { path: "payment/success", element: <LazyWrapper><ProtectedRoute><PaymentSuccessPage /></ProtectedRoute></LazyWrapper> },
      { path: "payment/failed", element: <LazyWrapper><ProtectedRoute><PaymentFailedPage /></ProtectedRoute></LazyWrapper> },
      { path: "breeding", element: <LazyWrapper><ProtectedRoute><Breeding /></ProtectedRoute></LazyWrapper> },
      { path: "addBreeding", element: <LazyWrapper><ProtectedRoute><AddBreeding /></ProtectedRoute></LazyWrapper> },
      { path: "breadingTable", element: <LazyWrapper><ProtectedRoute><BreadingTable /></ProtectedRoute></LazyWrapper> },
      { path: "editbreading/:id", element: <LazyWrapper><ProtectedRoute><EditBreeding /></ProtectedRoute></LazyWrapper> },
      { path: "animals", element: <LazyWrapper><ProtectedRoute><Animals /></ProtectedRoute></LazyWrapper> },
      { path: "editAnimal/:id", element: <LazyWrapper><ProtectedRoute><EditAnimal /></ProtectedRoute></LazyWrapper> },
      { path: "animalsDetails", element: <LazyWrapper><ProtectedRoute><AnimalsDetails /></ProtectedRoute></LazyWrapper> },
      { path: "animalCost", element: <LazyWrapper><ProtectedRoute><AnimalCost/></ProtectedRoute></LazyWrapper> },
      { path:"viewDetailsofAnimal/:id", element: <LazyWrapper><ProtectedRoute><ViewDetailsofAnimal /></ProtectedRoute></LazyWrapper> },

      { path: "mating", element: <LazyWrapper><ProtectedRoute><Mating /></ProtectedRoute></LazyWrapper> },
      { path:"editMating/:id", element: <LazyWrapper><ProtectedRoute><EditMating /></ProtectedRoute></LazyWrapper> },
      { path: "matingTable", element: <LazyWrapper><ProtectedRoute><MatingTable /></ProtectedRoute></LazyWrapper> },
      { path: "weight", element: <LazyWrapper><ProtectedRoute><Weight /></ProtectedRoute></LazyWrapper> },
      { path: "weightTable", element: <LazyWrapper><ProtectedRoute><WeightTable /></ProtectedRoute></LazyWrapper> },
      { path: "editWeight/:id", element: <LazyWrapper><ProtectedRoute><EditWeight /></ProtectedRoute></LazyWrapper> },
      { path: "addVaccine", element: <LazyWrapper><ProtectedRoute><Vaccinebyanimal/></ProtectedRoute></LazyWrapper> },
      { path: "vaccinebylocationshed", element: <LazyWrapper><ProtectedRoute><Vaccinebylocationshed/></ProtectedRoute></LazyWrapper> },
      { path: "Vaccinebyanimalsstable", element: <LazyWrapper><ProtectedRoute><Vaccinebyanimalsstable/></ProtectedRoute></LazyWrapper> },
      { path: "vaccineTable", element: <LazyWrapper><ProtectedRoute><VaccineTable/></ProtectedRoute></LazyWrapper> },
      { path: "editVaccine/:id", element: <LazyWrapper><ProtectedRoute><EditVaccine/></ProtectedRoute></LazyWrapper> },
      { path: "treatment", element: <LazyWrapper><ProtectedRoute><Treatment/></ProtectedRoute></LazyWrapper> },
      { path: "treatmentTable", element: <LazyWrapper><ProtectedRoute><TreatmentTable/></ProtectedRoute></LazyWrapper> },
      { path: "editTreatment/:id", element: <LazyWrapper><ProtectedRoute><EditTreatment/></ProtectedRoute></LazyWrapper> },
      { path: "treatmentLocation", element: <LazyWrapper><ProtectedRoute><TreatmentLocation/></ProtectedRoute></LazyWrapper> },
      { path: "treatmentAnimal", element: <LazyWrapper><ProtectedRoute><TreatmentAnimal/></ProtectedRoute></LazyWrapper> },
      { path: "treatAnimalTable", element: <LazyWrapper><ProtectedRoute><TreatAnimalTable/></ProtectedRoute></LazyWrapper> },
      { path: "editTreatAnimal/:id", element: <LazyWrapper><ProtectedRoute><EditTreatAnimal/></ProtectedRoute></LazyWrapper> },
      { path: "feed", element: <LazyWrapper><ProtectedRoute><Feed/></ProtectedRoute></LazyWrapper> },
      { path: "editfeed/:id", element: <LazyWrapper><ProtectedRoute><Editfeed/></ProtectedRoute></LazyWrapper> },
      { path: "editFeedbyLocation/:id", element: <LazyWrapper><ProtectedRoute><EditFeedbyLocation/></ProtectedRoute></LazyWrapper> },
      { path: "feedbylocation", element: <LazyWrapper><ProtectedRoute><Feedbylocation/></ProtectedRoute></LazyWrapper> },
      { path: "feedingTable", element: <LazyWrapper><ProtectedRoute><FeedingTable/></ProtectedRoute></LazyWrapper> },
      { path: "feedlocationtable", element: <LazyWrapper><ProtectedRoute><Feedlocationtable/></ProtectedRoute></LazyWrapper> },
      { path: "fodder", element: <LazyWrapper><ProtectedRoute><Fodder/></ProtectedRoute></LazyWrapper> },
      { path: "fodderTable", element: <LazyWrapper><ProtectedRoute><FodderTable/></ProtectedRoute></LazyWrapper> },
      { path: "editFodder/:id", element: <LazyWrapper><ProtectedRoute><EditFodder/></ProtectedRoute></LazyWrapper> },
      { path: "dashboard", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><Dashboard/></ProtectedRoute></LazyWrapper> },
      { path: "AdminDashboard", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminDashboard/></ProtectedRoute></LazyWrapper> },
      { path: "addVaccineType", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AddVaccineType/></ProtectedRoute></LazyWrapper> },
      { path: "vaccineTypetable", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><VaccineTypetable/></ProtectedRoute></LazyWrapper> },
      { path: "editVaccineType/:id", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><EditVaccineType/></ProtectedRoute></LazyWrapper> },
      { path: "addPlan", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AddPlan/></ProtectedRoute></LazyWrapper> },
      { path: "planTable", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><PlanTable/></ProtectedRoute></LazyWrapper> },
      { path: "editPlan/:id", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><EditPlan/></ProtectedRoute></LazyWrapper> },
      { path: "addDiscountCode", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AddDiscountCode/></ProtectedRoute></LazyWrapper> },
      { path: "discountCodeTable", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><DiscountCodeTable/></ProtectedRoute></LazyWrapper> },
      { path: "editDiscountCode/:id", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><EditDiscountCode/></ProtectedRoute></LazyWrapper> },

      // RBAC Routes - Roles
      { path: "roles", element: <LazyWrapper><PermissionProtectedRoute requiredPermissions={PERMISSIONS.ROLES_READ}><RolesList/></PermissionProtectedRoute></LazyWrapper> },
      { path: "roles/create", element: <LazyWrapper><PermissionProtectedRoute requiredPermissions={PERMISSIONS.ROLES_MANAGE}><RoleForm/></PermissionProtectedRoute></LazyWrapper> },
      { path: "roles/edit/:id", element: <LazyWrapper><PermissionProtectedRoute requiredPermissions={PERMISSIONS.ROLES_MANAGE}><RoleForm/></PermissionProtectedRoute></LazyWrapper> },

      // RBAC Routes - Employees
      { path: "employees", element: <LazyWrapper><PermissionProtectedRoute requiredPermissions={PERMISSIONS.EMPLOYEES_READ}><EmployeesList/></PermissionProtectedRoute></LazyWrapper> },
      { path: "employees/create", element: <LazyWrapper><PermissionProtectedRoute requiredPermissions={PERMISSIONS.EMPLOYEES_MANAGE}><EmployeeForm/></PermissionProtectedRoute></LazyWrapper> },
      { path: "employees/edit/:id", element: <LazyWrapper><PermissionProtectedRoute requiredPermissions={PERMISSIONS.EMPLOYEES_MANAGE}><EmployeeForm/></PermissionProtectedRoute></LazyWrapper> },
      { path: "employees/:id/roles", element: <LazyWrapper><PermissionProtectedRoute requiredPermissions={PERMISSIONS.EMPLOYEES_MANAGE}><EmployeeRoles/></PermissionProtectedRoute></LazyWrapper> },

      { path: "editVaccineanimals/:id", element: <LazyWrapper><ProtectedRoute><EditVaccineanimals/></ProtectedRoute></LazyWrapper> },
      { path: "vaccinebytagid", element: <LazyWrapper><ProtectedRoute><Vaccinebytagid/></ProtectedRoute></LazyWrapper> },

      { path: "reportServices", element: <LazyWrapper><ProtectedRoute><ReportServices/></ProtectedRoute></LazyWrapper> },
      { path: "animalServices", element: <LazyWrapper><ProtectedRoute><AnimalServices/></ProtectedRoute></LazyWrapper> },
      { path: "matingServices", element: <LazyWrapper><ProtectedRoute><MatingServices/></ProtectedRoute></LazyWrapper> },
      { path: "weightServices", element: <LazyWrapper><ProtectedRoute><WeightServices/></ProtectedRoute></LazyWrapper> },
      { path: "breedingServices", element: <LazyWrapper><ProtectedRoute><BreedingServices/></ProtectedRoute></LazyWrapper> },
      { path: "vaccineServices", element: <LazyWrapper><ProtectedRoute><VaccineServices/></ProtectedRoute></LazyWrapper> },
      { path: "excludedServices", element: <LazyWrapper><ProtectedRoute><ExcludedServices/></ProtectedRoute></LazyWrapper> },
      { path: "treetmentServices", element: <LazyWrapper><ProtectedRoute><TreetmentServices/></ProtectedRoute></LazyWrapper> },
      { path: "feedingServices", element: <LazyWrapper><ProtectedRoute><FeedingServices/></ProtectedRoute></LazyWrapper> },
      { path: "fodderServices", element: <LazyWrapper><ProtectedRoute><FodderServices/></ProtectedRoute></LazyWrapper> },
      { path: "matingLocation", element: <LazyWrapper><ProtectedRoute><MatingLocation/></ProtectedRoute></LazyWrapper> },
      { path: "locationPost", element: <LazyWrapper><ProtectedRoute><LocationPost/></ProtectedRoute></LazyWrapper> },
      { path: "locationTable", element: <LazyWrapper><ProtectedRoute><LocationTable/></ProtectedRoute></LazyWrapper> },
      { path: "editLocation/:id", element: <LazyWrapper><ProtectedRoute><EditLocation/></ProtectedRoute></LazyWrapper> },
      { path: "breedPost", element: <LazyWrapper><ProtectedRoute><BreedPost/></ProtectedRoute></LazyWrapper> },
      { path: "breedTable", element: <LazyWrapper><ProtectedRoute><BreedTable/></ProtectedRoute></LazyWrapper> },
      { path: "editBreed/:id", element: <LazyWrapper><ProtectedRoute><EditBreed/></ProtectedRoute></LazyWrapper> },
      { path: "locationServices", element: <LazyWrapper><ProtectedRoute><LocationServices/></ProtectedRoute></LazyWrapper> },
      { path: "breedServices", element: <LazyWrapper><ProtectedRoute><BreedServices/></ProtectedRoute></LazyWrapper> },

      { path: "notificationPage", element: <LazyWrapper><ProtectedRoute><NotificationPage/></ProtectedRoute></LazyWrapper> },
      { path: "support", element: <LazyWrapper><PublicSupportForm/></LazyWrapper> },
      { path: "support-old", element: <LazyWrapper><ProtectedRoute><Support/></ProtectedRoute></LazyWrapper> },
      
      // Support Tickets Routes - User (Authenticated)
      { path: "dashboard/support", element: <LazyWrapper><ProtectedRoute><UserTicketsList/></ProtectedRoute></LazyWrapper> },
      { path: "dashboard/support/:id", element: <LazyWrapper><ProtectedRoute><UserTicketDetails/></ProtectedRoute></LazyWrapper> },
      
      // Support Tickets Routes - Admin (Authenticated Admin)
      { path: "admin/support", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminTicketsList/></ProtectedRoute></LazyWrapper> },
      { path: "admin/support/:id", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminTicketDetails/></ProtectedRoute></LazyWrapper> },
      { path: "admin/support/statistics", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminSupportStatistics/></ProtectedRoute></LazyWrapper> },

      // Course Registration — public + student (no farm auth)
      {
        path: "course",
        children: [
          { index: true, element: <LazyWrapper><CourseLanding /></LazyWrapper> },
          { path: "register", element: <LazyWrapper><CourseRegister /></LazyWrapper> },
          { path: "register/success", element: <LazyWrapper><GroupRegistrationSuccess /></LazyWrapper> },
          { path: "login", element: <LazyWrapper><CourseLogin /></LazyWrapper> },
          {
            path: "dashboard",
            element: (
              <LazyWrapper>
                <CourseProtectedRoute>
                  <CourseLayout>
                    <CourseDashboard />
                  </CourseLayout>
                </CourseProtectedRoute>
              </LazyWrapper>
            ),
          },
          {
            path: "payment",
            element: (
              <LazyWrapper>
                <CourseProtectedRoute>
                  <CourseLayout>
                    <CoursePayment />
                  </CourseLayout>
                </CourseProtectedRoute>
              </LazyWrapper>
            ),
          },
          {
            path: "lectures",
            element: (
              <LazyWrapper>
                <CourseProtectedRoute>
                  <CourseLayout>
                    <CourseLectures />
                  </CourseLayout>
                </CourseProtectedRoute>
              </LazyWrapper>
            ),
          },
          {
            path: "profile",
            element: (
              <LazyWrapper>
                <CourseProtectedRoute>
                  <CourseLayout>
                    <CourseProfile />
                  </CourseLayout>
                </CourseProtectedRoute>
              </LazyWrapper>
            ),
          },
        ],
      },

      // Course Registration — admin
      { path: "admin/course", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminCourseDashboard /></ProtectedRoute></LazyWrapper> },
      { path: "admin/course/students", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminCourseStudents /></ProtectedRoute></LazyWrapper> },
      { path: "admin/course/registrations", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminCourseRegistrations /></ProtectedRoute></LazyWrapper> },
      { path: "admin/course/registrations/:id", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminCourseRegistrationDetails /></ProtectedRoute></LazyWrapper> },
      { path: "admin/course/payments", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminCoursePayments /></ProtectedRoute></LazyWrapper> },
      { path: "admin/course/payments/:id", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminCoursePaymentDetails /></ProtectedRoute></LazyWrapper> },
      { path: "admin/course/lectures", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminCourseLectures /></ProtectedRoute></LazyWrapper> },
      { path: "admin/course/payment-settings", element: <LazyWrapper><ProtectedRoute allowedRoles={['admin']}><AdminCoursePaymentSettings /></ProtectedRoute></LazyWrapper> },
      { path: "pharmacy", element: <LazyWrapper><ProtectedRoute><Pharmacy/></ProtectedRoute></LazyWrapper> },
      { path: "withGrowthData", element: <LazyWrapper><ProtectedRoute><WithGrowthData/></ProtectedRoute></LazyWrapper> },
      { path: "supplier", element: <LazyWrapper><ProtectedRoute><Supplier/></ProtectedRoute></LazyWrapper> },
      { path: "supplierTable", element: <LazyWrapper><ProtectedRoute><SupplierTable/></ProtectedRoute></LazyWrapper> },
      { path: "editSupplier/:id", element: <LazyWrapper><ProtectedRoute><EditSupplier/></ProtectedRoute></LazyWrapper> },
      { path: "SupplierServices", element: <LazyWrapper><ProtectedRoute><SupplierServices/></ProtectedRoute></LazyWrapper> },
      { path:"detailsLocation/:id", element: <LazyWrapper><ProtectedRoute><DetailsLocation /></ProtectedRoute></LazyWrapper> },
      { path:"userDashboard", element: <LazyWrapper><ProtectedRoute><UserDashboard /></ProtectedRoute></LazyWrapper> },
      { path:"profile", element: <LazyWrapper><ProtectedRoute><Profile /></ProtectedRoute></LazyWrapper> },
      { path: "notificationPage", element: <ProtectedRoute><NotificationPage/></ProtectedRoute> },
      { path: "support", element: <ProtectedRoute><Support/></ProtectedRoute> },
      { path: "pharmacy", element: <ProtectedRoute><Pharmacy/></ProtectedRoute> },
      { path: "withGrowthData", element: <ProtectedRoute><WithGrowthData/></ProtectedRoute> },
      { path: "supplier", element: <ProtectedRoute><Supplier/></ProtectedRoute> },
      { path: "supplierTable", element: <ProtectedRoute><SupplierTable/></ProtectedRoute> },
      { path: "editSupplier/:id", element: <ProtectedRoute><EditSupplier/></ProtectedRoute> },
      { path: "SupplierServices", element: <ProtectedRoute><SupplierServices/></ProtectedRoute> },
      { path:"detailsLocation/:id", element: <ProtectedRoute><DetailsLocation /></ProtectedRoute> },
      { path:"weeklyNotifications", element: <ProtectedRoute><WeeklyNotifications /></ProtectedRoute> },
      

    ],
  },
]);





export default function App() {

  let {setAuthorization} = useContext(UserContext);
   const { i18n } = useTranslation();

  useEffect(() => {
    // Ensure language is set correctly
    const savedLang = localStorage.getItem('lang');
    const lang = savedLang && ['en', 'ar', 'ur'].includes(savedLang) ? savedLang : 'ar';
    
    // Set i18n language if it's different
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
    
    const dir = lang === 'ar' || lang === 'ur' ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }, [i18n.language, i18n]);
  
  useEffect(()=>{
    const token = getToken();
    if(token !== null){
      setAuthorization(token)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  return <>
      <Suspense fallback={<PageLoading variant="fullscreen" />}>
      <CourseAuthProvider>
      <DiscountCodeContextProvider>
      <PlanContextProvider>
      <VaccineTypeContextProvider>
  <AdminContextProvider>
     <SidebarProvider>
        <SubscriptionProvider>
        <SupplierContextProvider>
        <NewvaccineContextProvider>
    <VaccinetableentriescontextProvider>
  <BreedContextProvider>
  <LocationContextshedProvider>
  <LocationContextProvider>
  <DashboardContextProvider>
  <FeedbyLocationContextProvider>
  <FeedContextProvider>
  <TreatmentContextProvider>
  <ExcludedContextProvider>
  <VaccineanimalContextProvider>
  <GetAnimalContextProvider>
  <BreedingcontextProvider>
  <WeightContextProvider>
  <MatingContextProvider>
<AnimalContextProvider>

  <RouterProvider router={routers}>
  </RouterProvider>

  </AnimalContextProvider>
  </MatingContextProvider>
  </WeightContextProvider>
  </BreedingcontextProvider>
  </GetAnimalContextProvider>
  </VaccineanimalContextProvider>
  </ExcludedContextProvider>
  </TreatmentContextProvider>
  </FeedContextProvider>
  </FeedbyLocationContextProvider>
  </DashboardContextProvider>
  </LocationContextProvider>
  </LocationContextshedProvider>
  </BreedContextProvider>
  </VaccinetableentriescontextProvider>
        </NewvaccineContextProvider>
</SupplierContextProvider>
        </SubscriptionProvider>
  </SidebarProvider>
  </AdminContextProvider>
  </VaccineTypeContextProvider>
  </PlanContextProvider>
  </DiscountCodeContextProvider>
  </CourseAuthProvider>
  </Suspense>
  

 

  </>
}